import moment from 'moment';
import { EarningTypes, PayrollFrequencies, PayrollStatus, PayrollTypes } from '../types/checkHQPayroll/payroll.types';
import { BenefitPeriod, BenefitsList, IntegratedBenefitType } from '../types/employee/benefits.types';

export const PayrollFrequencyMap: Record<string, string> = {
  [PayrollFrequencies.WEEKLY]: 'weekly',
  [PayrollFrequencies.BI_WEEKLY]: 'biweekly',
  [PayrollFrequencies.MONTHLY]: 'monthly',
};

export const EarningTypesMap: Record<string, string> = {
  Salary: 'salaried',
  [EarningTypes.Hourly]: 'hourly',
  salaried: 'Salary',
  [EarningTypes.Overtime]: 'overtime',
  overtime: [EarningTypes.Overtime].toString(),
  [EarningTypes.DoubleOvertime]: 'double_overtime',
  [EarningTypes.PaidHoliday]: 'paid_holiday',
  [EarningTypes.Pto]: 'pto',
  [EarningTypes.Sick]: 'sick',
  [EarningTypes.Piece]: 'piece',
  [EarningTypes.RestAndRecovery]: 'rest_and_recovery',
  [EarningTypes.NonProductive]: 'non_productive',
  double_overtime: [EarningTypes.DoubleOvertime].toString(),
  paid_holiday: [EarningTypes.PaidHoliday].toString(),
  pto: [EarningTypes.Pto].toString(),
  sick: [EarningTypes.Sick].toString(),
  piece: [EarningTypes.Piece].toString(),
  rest_and_recovery: [EarningTypes.RestAndRecovery].toString(),
  non_productive: [EarningTypes.NonProductive].toString(),
  'Weekly Overtime': 'hourly',
  cash_tips: EarningTypes.CashTips,
  paycheck_tips: EarningTypes.PaycheckTips,
  bonus: EarningTypes.Bonus,
  commission: EarningTypes.Commission,
  group_term_life: EarningTypes.GroupTermLife,
  other_imputed: EarningTypes.OtherImputed,
  severance: EarningTypes.Severance,
  non_hourly_regular: EarningTypes.NonHourlyRegular,
  '2_percent_shareholder_benefits': EarningTypes.ShareholderBenefits2,
  '2_percent_shareholder_hsa': EarningTypes.ShareholderHsa2,
  [EarningTypes.CashTips]: 'cash_tips',
  [EarningTypes.PaycheckTips]: 'paycheck_tips',
  [EarningTypes.NonHourlyRegular]: 'non_hourly_regular',
  [EarningTypes.Bonus]: 'bonus',
  [EarningTypes.Commission]: 'commission',
  [EarningTypes.GroupTermLife]: 'group_term_life',
  [EarningTypes.OtherImputed]: 'other_imputed',
  [EarningTypes.Severance]: 'severance',
  [EarningTypes.ShareholderBenefits2]: '2_percent_shareholder_benefits',
  [EarningTypes.ShareholderHsa2]: '2_percent_shareholder_hsa',
};

export const CheckHqKeyMapperUI: Record<string, string> = {
  paymentMethod: 'Payment Method',
  direct_deposit: 'Direct Deposit',
  payday: 'Payday',
  periodStarts: 'Period Starts',
  periodEnds: 'Period Ends',
  approvalDeadline: 'Approval Deadline',
  processingPeriod: 'Processing Period',
  type: 'Type',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  netPay: 'Gross Pay',
  totalHoursWorked: 'Total Hours Worked',
  overtimeHours: 'Overtime Hours',
  [PayrollStatus.PROCESSING]: 'Processing',
  [PayrollStatus.DRAFT]: 'Draft',
  [PayrollStatus.FAILED]: 'Failed',
  [PayrollStatus.PARTIAL]: 'Partial',
  [PayrollStatus.PAID]: 'Paid',
  [PayrollStatus.PENDING]: 'Pending',
  four_day: '4 Days',
  three_day: '3 Days',
  two_day: '2 Days',
  amendment: 'Amendment',
  off_cycle: 'Off Cycle',
  regular: 'Regular',
  company: 'Company',
  employee: 'Employee',
  manual: 'Manual',
  [PayrollTypes.OffCycle]: 'off_cycle',
  [PayrollTypes.Amendment]: 'amendment',
  [PayrollTypes.Regular]: 'regular',
  directDepositTotal: 'Direct Deposits Total',
  manualTotal: 'Manual Checks Total',
  totalEmployees: 'Total Employees',
};

export const BenefitsListMapperUI: Record<string, string> = {
  [BenefitsList.One_Twenty_Five_MEDICAL]: '125 Medical',
  [BenefitsList.One_Twenty_Five_VISION]: '125 Vision',
  [BenefitsList.One_Twenty_Five_DENTAL]: '125 Dental',
  [BenefitsList.One_Twenty_Five_Disability]: '125 Disability',
  [BenefitsList.One_Twenty_Five_Accident]: '125 Accident',
  [BenefitsList.One_Twenty_Five_Cancer]: '125 Cancer',
  [BenefitsList.One_Twenty_Five_Critical_Illness]: '125 Critical Illness',
  [BenefitsList.One_Twenty_Five_Hospital]: '125 Hospital',
  [BenefitsList.One_Twenty_Five_Life]: '125 Life',
  [BenefitsList.One_Twenty_Five_Medical_Other]: '125 Medical Other',
  [BenefitsList.Four_O_One_K]: '401k',
  [BenefitsList.Four_O_Three_B]: '403b',
  [BenefitsList.Four_Five_Seven]: '457',
  [BenefitsList.Roth_Four_O_One_K]: 'Roth 401k',
  [BenefitsList.Roth_Four_O_Three_B]: 'Roth 403b',
  [BenefitsList.Roth_Four_Five_Seven]: 'Roth 457',
  [BenefitsList.Fsa_Medical]: 'FSA Medical',
  [BenefitsList.Fsa_Dependent_Care]: 'FSA Dependent Care',
  [BenefitsList.HSA]: 'HSA',
  [BenefitsList.Simple_IRA]: 'Simple IRA',
  [BenefitPeriod.MONTHLY]: 'Monthly',
  [BenefitPeriod.NONE]: 'None',
};

export const BenefitsCheckHqMapper: Record<string, string> = {
  companyContributionAmount: 'company_contribution_amount',
  employeeContributionAmount: 'employee_contribution_amount',
  companyContributionPercent: 'company_contribution_percent',
  employeeContributionPercent: 'employee_contribution_percent',
  employee_contribution_percent: 'employeeContributionPercent',
  company_contribution_percent: 'companyContributionPercent',
  company_contribution_amount: 'companyContributionAmount',
  employee_contribution_amount: 'employeeContributionAmount',
  benefitType: 'benefit',
  benefit: 'benefitType',
  [BenefitPeriod.MONTHLY]: 'monthly',
  [BenefitPeriod.NONE]: 'none',
  monthly: BenefitPeriod.MONTHLY,
  none: BenefitPeriod.NONE,
};

export const IntegratedBenefitKey = {
  [IntegratedBenefitType.SIMPLY_INSURED]: 'int_Xf90bJAKE9Lf2jVjbgne',
  [IntegratedBenefitType.NEXT_INSURANCE]: 'int_Fffk6nOGSHtuCTirWqlp',
  [IntegratedBenefitType.GUIDELINE]: 'int_isqM1DgkMdj6DOPXOwgd',
};

export const convertDateToCheckHQFormat = (date: Date): string => {
  return moment(date).format('YYYY-MM-DD');
};

export const FALLBACK_OVERTIME_RATE = 1.5;
export const FALLBACK_DOUBLE_OVERTIME_RATE = 2.0;
export const FALLBACK_WEEKLY_OVERTIME_RATE = 1.5;
